import { useEffect } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { ControlledCheckboxAutoComplete } from 'components/shared/ControlledFields';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useGetRosterFunctionsByCostCenterAndDate from './hooks/useGetRosterFunctionsByCostCenterAndDate';
import useGetRosterFunctionsPreferred from './hooks/useGetRosterFunctionsPreferred';
import useGetRosterQualifications from './hooks/useGetRosterQualifications';
import useGetRosterQualificationsPreferred from './hooks/useGetRosterQualificationsPreferred';
import useAddRosterFunctions from './hooks/useAddRosterFunctions';
import useRemoveRosterFunctions from './hooks/useRemoveRosterFunctions';
import useAddRosterQualifications from './hooks/useAddRosterQualifications';
import useRemoveRosterQualifications from './hooks/useRemoveRosterQualifications';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';
import {
  GridContainer,
  GridCompact6
} from 'components/shared/Grid/Grid.components';

const schema = yup.object({
  rosterFunctions: yup.array().required().default([]),
  rosterQualifications: yup.array().required().default([])
});

type FormValues = yup.InferType<typeof schema>;
type RosterType = 'rosterFunctions' | 'rosterQualifications';

const Preferences = ({ costCenterId, referenceDate }) => {
  const { t } = useTranslation('common');
  const { control, setValue, watch } = useForm<FormValues>({
    defaultValues: schema.cast({
      rosterFunctions: [],
      rosterQualifications: []
    }),
    resolver: yupResolver(schema)
  });

  const startDate = dayjs(referenceDate).isValid()
    ? dayjs(referenceDate).toISOString()
    : '';
  const endDate = startDate;

  const { data: rosterFunctions } = useGetRosterFunctionsByCostCenterAndDate({
    costCenterId,
    startDate,
    endDate
  });
  const { data: preferredFunctions, isFetching: isFetchingFunctions } =
    useGetRosterFunctionsPreferred({ costCenterId, startDate, endDate });
  const { data: rosterQualifications } = useGetRosterQualifications({
    costCenterId,
    startDate,
    endDate
  });
  const {
    data: preferredQualifications,
    isFetching: isFetchingQualifications
  } = useGetRosterQualificationsPreferred({ costCenterId });

  const addRosterFunction = useAddRosterFunctions();
  const removeRosterFunction = useRemoveRosterFunctions();
  const addRosterQualification = useAddRosterQualifications();
  const removeRosterQualification = useRemoveRosterQualifications();

  const rosterFunctionsWatch = watch('rosterFunctions');
  const rosterQualificationsWatch = watch('rosterQualifications');

  // Set default values
  useEffect(() => {
    if (preferredFunctions && preferredQualifications) {
      const defaultFunctions = preferredFunctions.flatMap(
        (item) => item?.functions?.map((func) => func.id)
      );
      const defaultQualifications = Array.isArray(preferredQualifications)
        ? preferredQualifications.map((item) => item.id)
        : [];

      setValue('rosterFunctions', defaultFunctions);
      setValue('rosterQualifications', defaultQualifications);
    }
  }, [preferredFunctions, preferredQualifications, setValue]);

  const handleMutation = (
    item: string | number,
    type: RosterType,
    mutation: (payload: any, options: any) => void,
    currentValues: Array<string | number>,
    isAdd: boolean
  ) => {
    const payload = {
      costCenterId,
      [type === 'rosterFunctions' ? 'functionId' : 'qualificationId']: item
    };

    mutation(payload, {
      onSuccess: () => {
        setValue(
          type,
          isAdd
            ? [...currentValues, item]
            : currentValues.filter((v) => v !== item)
        );
      }
    });
  };

  // handle change inside the dropdown checkbox
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: RosterType
  ) => {
    const items = Array.from(event as unknown as Set<string | number>);

    const isFunctionType = type === 'rosterFunctions';
    const currentValues = isFunctionType
      ? rosterFunctionsWatch
      : rosterQualificationsWatch;

    const addedItem = items.find((item) => !currentValues.includes(item));
    const removedItem = currentValues.find((item) => !items.includes(item));

    if (addedItem) {
      handleMutation(
        addedItem,
        type,
        isFunctionType
          ? addRosterFunction.mutate
          : addRosterQualification.mutate,
        currentValues,
        true
      );
    }

    if (removedItem) {
      handleMutation(
        removedItem,
        type,
        isFunctionType
          ? removeRosterFunction.mutate
          : removeRosterQualification.mutate,
        currentValues,
        false
      );
    }
  };

  // handle remove in the list below the dropdownlist
  const handleRemove = (idToRemove: string | number, type: RosterType) => {
    const isFunctionType = type === 'rosterFunctions';

    handleMutation(
      idToRemove,
      type,
      isFunctionType
        ? removeRosterFunction.mutate
        : removeRosterQualification.mutate,
      isFunctionType ? rosterFunctionsWatch : rosterQualificationsWatch,
      false
    );
  };

  return (
    <StyledPaper>
      <GridContainer>
        <GridCompact6>
          <Typography variant="h6" marginBottom={2}>
            {t('tableTabs.FUNCTIONS')}
          </Typography>
          {isFetchingFunctions ? (
            <Skeleton variant="rounded" width="100%" height="40px" />
          ) : (
            <ControlledCheckboxAutoComplete
              fullWidth
              name="rosterFunctions"
              label={t('tableTabs.FUNCTIONS')}
              variant="outlined"
              options={rosterFunctions ?? []}
              control={control}
              idProperty="id"
              labelProperty="description"
              allowItemRemoval
              onChange={(event) => handleChange(event, 'rosterFunctions')}
              onRemoveElement={(idToRemove) =>
                handleRemove(idToRemove, 'rosterFunctions')
              }
            />
          )}
        </GridCompact6>
        <GridCompact6>
          <Typography variant="h6" marginBottom={2}>
            {t('qualifications')}
          </Typography>
          {isFetchingQualifications ? (
            <Skeleton variant="rounded" width="100%" height="40px" />
          ) : (
            <ControlledCheckboxAutoComplete
              fullWidth
              name="rosterQualifications"
              label={t('qualifications')}
              variant="outlined"
              options={rosterQualifications ?? []}
              control={control}
              idProperty="id"
              labelProperty="name"
              allowItemRemoval
              onChange={(event) => handleChange(event, 'rosterQualifications')}
              onRemoveElement={(idToRemove) =>
                handleRemove(idToRemove, 'rosterQualifications')
              }
            />
          )}
        </GridCompact6>
      </GridContainer>
    </StyledPaper>
  );
};

export default Preferences;
